/**
 * Perguntas frequentes de preço e contratação.
 *
 * Uma fonte só para a página de Preços e para o `FAQPage` em JSON-LD. Se a
 * resposta mudar num lugar e não no outro, o Google mostra uma coisa e o site
 * outra, e isso é motivo para o rich result sumir.
 *
 * `answer` é texto puro, porque vai para o JSON-LD. `chatLink` marca a resposta
 * em que "chat do site" vira botão que abre o chat na página.
 */
export interface FaqEntry {
  question: string;
  answer: string;
  chatLink?: boolean;
}

export const pricingFaq: FaqEntry[] = [
  {
    question: 'Tem teste grátis ou plano gratuito?',
    answer:
      'Não tem. Você escolhe o plano, assina e começa a usar. Se quiser tirar dúvida antes, fala com a gente no chat do site ou no WhatsApp. A gente responde rápido.',
    chatLink: true,
  },
  {
    question: 'Existe fidelidade ou multa para sair?',
    answer:
      'Não. A assinatura é mensal. Cancela no painel quando quiser, sem multa e sem contrato amarrado.',
  },
  {
    question: 'Como funciona o custo da Meta?',
    answer:
      'A Meta cobra pelas mensagens de modelo entregues (marketing sempre; utilidade e autenticação fora da janela de 24 horas de atendimento). Esse valor aparece na sua conta, separado da assinatura da Operax. A gente não embute taxa escondida por cima.',
  },
  {
    question: 'O que são os apps e quando eu pago por eles?',
    answer:
      'O plano já traz atendimento e funil. Agenda, estoque, Pix, NFS-e e outros módulos ficam no Portal de Apps. Você ativa só o que precisa, quando precisar. O preço do add-on aparece no catálogo antes de confirmar.',
  },
  {
    question: 'Posso começar enxuto e ativar apps depois?',
    answer:
      'Pode. Muita gente começa só com o núcleo e liga agenda ou Pix quando a operação pede. Sem migração e sem trocar de sistema.',
  },
  {
    question: 'Quanto tempo leva pra configurar?',
    answer:
      'A configuração é por demanda, com a conta da própria empresa ou da pessoa que vai operar. Depois de assinar, você conecta o WhatsApp no painel e já atende. Apps extras entram no mesmo ritmo: ativa e usa. Não tem projeto longo nem implantação obrigatória.',
  },
  {
    question: 'Como conecto meu WhatsApp?',
    answer:
      'Pela API oficial da Meta. A Operax é Provedor de Tecnologia verificado pela Meta. Depois de entrar no painel, um assistente guia a conexão do número. É o WhatsApp oficial, não gambiarra de celular plugado.',
  },
  {
    question: 'Preciso de um número novo ou uso o que já tenho?',
    answer:
      'Na maioria dos casos dá pra usar o número da empresa. A Meta tem regras de migração e verificação. Se o seu número já estiver em outro provedor, a gente te orienta no chat antes de mexer.',
  },
  {
    question: 'Posso ter mais de um usuário ou canal?',
    answer:
      'Sim. Cada plano traz um pacote de usuários e conexões. Se precisar de mais gente no atendimento, você adiciona no painel. O valor do extra aparece antes de confirmar.',
  },
  {
    question: 'Se eu cancelar, o que acontece com meus dados?',
    answer:
      'Você leva o histórico. Cancela no painel, exporta o que precisar e encerra. Sem fidelidade e sem surpresa na fatura seguinte.',
  },
  {
    question: 'Serve pra clínica, imobiliária ou outro setor?',
    answer:
      'O núcleo (conversas e funil) é o mesmo. O que muda são os apps, campos e automações do seu segmento. Dá pra ver exemplos na página de setores.',
  },
  {
    question: 'Ainda tenho dúvida. O que eu faço?',
    answer:
      'Abre o chat no canto da tela ou manda no WhatsApp. Se preferir falar com alguém do time, responde em horário comercial no mesmo canal ou em contato@operax.digital.',
  },
];

/**
 * Perguntas da home. Mesma regra do `pricingFaq`: texto puro, uma fonte só para
 * a tela e para o `FAQPage` do JSON-LD.
 */
export const homeFaq: FaqEntry[] = [
  {
    question: 'A Operax cobra taxa por mensagem?',
    answer:
      'Não. A Meta cobra as mensagens de modelo direto na conta da sua empresa. A Operax cobra só a assinatura, sem taxa escondida por cima.',
  },
  {
    question: 'Existe fidelidade ou multa para sair?',
    answer: 'Não. A assinatura é mensal. Cancela no painel quando quiser e leva o seu histórico.',
  },
  {
    question: 'Quando eu pago pelos apps?',
    answer:
      'O plano já traz atendimento e funil. Os apps você ativa quando precisar, e o preço aparece no catálogo antes de confirmar. Vários você ativa sem custo.',
  },
  {
    question: 'Posso usar o número que já tenho?',
    answer:
      'Em muitos casos, sim. Com a coexistência da Meta, o número continua no aplicativo WhatsApp Business e passa a funcionar também pela API. A Meta decide, número a número, se ele pode usar a coexistência.',
  },
  {
    question: 'Quanto tempo leva para configurar?',
    answer:
      'Depois de assinar, um assistente guia a conexão do WhatsApp no painel e você já atende. Sem projeto longo nem implantação obrigatória.',
  },
  {
    question: 'Tem teste grátis?',
    answer:
      'Não. Desde o primeiro dia você opera com o WhatsApp oficial conectado à Meta, com número e modelos de mensagem de verdade, sem ambiente de demonstração. Se não gostar, devolvemos 100% do valor se você cancelar em até 7 dias da primeira contratação, como preveem os Termos de Uso. Ficou alguma dúvida antes de assinar? Chame a gente no WhatsApp.',
  },
];
